// Shapes category-3 (owner info) tax comments into the rows the History tab's Owner History
// view renders. Everything here is pure so it can be unit tested without a component.

// Template 3 is 'XXXX was changed by user ZZZZ, Wkst-### on MM/DD/YYYY', with XXXX filled in
// as the comma-joined field labels. The date the backend writes is 'MM/DD/YYYY h:mm A'
// (getLocalTimeStr), and whatever the user typed in the comment dialog - plus a trailing
// ' Per <reason>' when a reason code was picked - is appended after it.
const FIELD_LIST_MARKER = ' was changed by user ';
const TIMESTAMP_RE = /\son\s\d{1,2}\/\d{1,2}\/\d{4}\s\d{1,2}:\d{2}\s?[AP]M/i;

// Splits the stored comment into the field names it announces and the free text the user
// added. Used only for rows written before ownerChanges existed; newer rows carry both
// structured. Never throws - a comment that does not match either shape (the Assessor
// update writes its own fixed sentence) comes back with no fields and the whole text as
// the note, which is still the most useful thing to show.
export const parseOwnerChangeComment = (comment) => {
  const text = (comment || '').trim();
  if (!text) return { fields: [], note: '' };

  const markerAt = text.indexOf(FIELD_LIST_MARKER);
  const fields = markerAt > 0
    ? text.slice(0, markerAt).split(',').map((f) => f.trim()).filter(Boolean)
    : [];

  // With a field list parsed, the announcement is already on screen as chips, so the note is
  // only what follows the timestamp. Searching from the marker keeps a date the user typed
  // into their own note from being mistaken for the stamp.
  //
  // With no field list there is nothing on screen yet, so the whole sentence is the note -
  // the Assessor update writes its own fixed wording ('Mailling address was changed from
  // Assessor data by user ...') and a converted county's legacy rows are free text. Both
  // read as the reason for the change, which is exactly what this column is for.
  if (markerAt <= 0) return { fields, note: text };

  const tail = text.slice(markerAt);
  const stamp = tail.match(TIMESTAMP_RE);
  const note = stamp ? tail.slice(stamp.index + stamp[0].length).trim() : '';

  return { fields, note };
};

// The field labels to show as chips: the structured ownerChanges when the row has them,
// otherwise the ones named in the comment text.
export const ownerChangeFieldLabels = (row) => {
  const structured = (row && row.ownerChanges) || [];
  if (structured.length) return structured.map((c) => c.field).filter(Boolean);
  return parseOwnerChangeComment(row && row.comment).fields;
};

// beforeChange/afterChange are consolidated address blocks joined with '\n' - see
// getConsolidateView on the backend. A line holding only whitespace is a real, deliberate
// blank in that block (an empty address3 between two filled lines), so it is kept: dropping
// it would slide the lines under it up and mis-pair them against the other side.
const toLines = (block) => ((block || '').split('\n'));

// Pairs the before and after blocks line by line and marks the ones that moved, so the grid
// can highlight exactly what changed. Positional pairing is safe here because both sides
// come from the same getConsolidateView field order (name, C/O, address, city-state-zip).
// The shorter block is padded so an added or removed line still shows up on both sides.
export const diffOwnerBlocks = (beforeChange, afterChange) => {
  const before = toLines(beforeChange);
  const after = toLines(afterChange);
  const len = Math.max(before.length, after.length);
  const rows = [];
  for (let i = 0; i < len; i += 1) {
    const from = before[i] === undefined ? '' : before[i];
    const to = after[i] === undefined ? '' : after[i];
    rows.push({ from, to, changed: from.trim() !== to.trim() });
  }
  return rows;
};

// One display row per comment, newest first.
const toOwnerHistoryRow = (comment) => ({
  _id: comment._id,
  taxYear: comment.taxYear,
  taxId: comment.taxId,
  changedOn: comment.changedOn || comment.createdAt,
  userName: comment.userName || '',
  wkstNumber: comment.wkstNumber,
  fields: ownerChangeFieldLabels(comment),
  note: parseOwnerChangeComment(comment.comment).note,
  lines: diffOwnerBlocks(comment.beforeChange, comment.afterChange),
});

// Groups the comments under the tax record they belong to, one group per year of the record.
// `taxRecords` is the same year list the yearly tax history grid shows, so a year with no
// owner changes still gets a header saying so rather than silently disappearing.
export const groupOwnerHistoryByYear = (taxRecords, comments) => {
  const byKey = new Map();
  (comments || []).forEach((c) => {
    const key = `${c.taxYear}-${c.taxId}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(toOwnerHistoryRow(c));
  });

  return (taxRecords || [])
    .slice()
    .sort((a, b) => Number(b.taxYear || 0) - Number(a.taxYear || 0))
    .map((rec) => {
      const rows = (byKey.get(`${rec.taxYear}-${rec.taxId}`) || [])
        .sort((a, b) => new Date(b.changedOn) - new Date(a.changedOn));
      return {
        key: `${rec.taxYear}-${rec.taxId}`,
        taxYear: rec.taxYear,
        taxId: rec.taxId,
        rows,
      };
    });
};

export default {};
